import { wrapMiddlewaresWithSentry } from '@sentry/tanstackstart-react';
import { projectService } from "@acme/api";
import { renderMarkdownToHtml } from "@acme/mdx/markdown";
import { createServerFn } from "@tanstack/react-start";
import { object, optional, string } from "valibot";
import { dbMiddleware } from "~/lib/middleware/db";
import { setPreviewResponseHeaders } from "~/lib/server/blog-preview";
import { loadProjectPreview } from "~/lib/server/project-preview";
import { baseLocale, isLocale } from "~/paraglide/runtime.js";

function resolveLocale(locale?: string): string {
  return locale && isLocale(locale) ? locale : baseLocale;
}

export const $getAllPublicProjects = createServerFn({ method: "GET" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(optional(object({ locale: optional(string()) })))
  .handler((ctx) =>
    projectService.getAllPublic(ctx.context.db, {
      locale: resolveLocale(ctx.data?.locale),
    })
  );

export const $getProjectBySlug = createServerFn({ method: "GET" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(object({ locale: optional(string()), slug: string() }))
  .handler(async (ctx) => {
    const project = await projectService.getBySlug(ctx.context.db, {
      locale: resolveLocale(ctx.data.locale),
      slug: ctx.data.slug,
    });
    const contentHtml = await renderMarkdownToHtml(project.content ?? "");
    return { ...project, contentHtml };
  });

/**
 * A reviewer link's project: any project, draft or not, for a valid,
 * unexpired preview token. A bad token throws "Preview not found"; a deleted
 * project throws "Project not found". The route turns both into a 404.
 *
 * Like `$getArticlePreview`, the token is read in the handler and through
 * `loadProjectPreview`, which only the handler imports, so neither the
 * verification code nor the secret reaches the browser bundle.
 */
export const $getProjectPreview = createServerFn({ method: "GET" })
  .middleware(wrapMiddlewaresWithSentry({ dbMiddleware }))
  .validator(object({ locale: optional(string()), token: string() }))
  .handler((ctx) => {
    setPreviewResponseHeaders();
    return loadProjectPreview(ctx.context.db, {
      locale: resolveLocale(ctx.data.locale),
      token: ctx.data.token,
    });
  });

/** What the project page and the reviewer preview both render. */
export type PublicProject = Awaited<ReturnType<typeof $getProjectBySlug>>;
/** One entry of the public project list, as "more to explore" shows it. */
export type PublicProjectSummary = Awaited<
  ReturnType<typeof $getAllPublicProjects>
>[number];
